import React, { useReducer, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Button from './common/Button';
import { SurveyResponse } from '../types/lead';
import { Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { WHATSAPP_GROUP_LINK } from '../config/constants';
import { formReducer } from '../reducers/formReducer';
import { initialFormState } from '../types/form';

interface SurveyFormProps {
  email: string;
  lancamento?: number;
}

interface Question {
  id: keyof Omit<SurveyResponse, 'id' | 'created_at' | 'email' | 'lancamento'>;
  title: string;
  description?: string;
  type: 'select' | 'checkbox' | 'textarea';
  options?: Array<{ value: string; label: string }>;
}

const questions: Question[] = [
  {
    id: 'programming_experience',
    title: 'Onde você está hoje na sua jornada com programação?',
    description: 'Escolha a opção que melhor descreve seu momento atual',
    type: 'select',
    options: [
      { value: 'iniciante_total', label: 'Nunca programei, tô começando do zero' },
      { value: 'tentei_sozinho', label: 'Já tentei aprender sozinho, mas fiquei travado' },
      { value: 'sei_basico', label: 'Sei o básico, mas não consigo aplicar direito' },
      { value: 'tenho_projetos', label: 'Já programei projetos, mas sinto que falta direção' },
      { value: 'quero_especializar', label: 'Quero me especializar e sair do raso' }
    ]
  },
  {
    id: 'challenges',
    title: 'Qual desses desafios mais te trava hoje?',
    description: 'Selecione todos que se aplicam ao seu caso',
    type: 'checkbox',
    options: [
      { value: 'nao_sei_comecar', label: 'Não sei por onde começar' },
      { value: 'pulando_conteudo', label: 'Fico pulando de conteúdo e não consigo seguir uma linha' },
      { value: 'dificuldade_java', label: 'Não entendo bem o Java e os conceitos' },
      { value: 'falta_foco', label: 'Tenho dificuldade de manter o foco e a prática' },
      { value: 'medo_vagas', label: 'Tenho medo de não conseguir vaga depois' }
    ]
  },
  {
    id: 'main_goal',
    title: 'Qual é seu maior objetivo ao aprender Java?',
    description: 'Escolha o objetivo que mais te motiva',
    type: 'select',
    options: [
      { value: 'entrar_area_tech', label: 'Entrar na área de tecnologia' },
      { value: 'mudar_carreira', label: 'Mudar de carreira urgentemente' },
      { value: 'primeira_vaga_backend', label: 'Conquistar minha primeira vaga de dev backend' },
      { value: 'virar_especialista', label: 'Me tornar um especialista valorizado' },
      { value: 'criar_projetos', label: 'Criar meus próprios projetos com Java' }
    ]
  },
  {
    id: 'blocking_factor',
    title: 'Se eu pudesse te entregar uma coisa na Java Dev Week que desbloqueasse seu caminho, o que seria?',
    description: 'Seja específico(a) - isso vai me ajudar a criar um conteúdo mais relevante para você',
    type: 'textarea'
  },
  {
    id: 'notification_preference',
    title: 'Você gostaria de ser avisado quando as vagas da Jornada Java abrirem?',
    description: 'Escolha como quer ser notificado sobre as novidades',
    type: 'select',
    options: [
      { value: 'quero_prioridade', label: '✅ Sim, quero prioridade na abertura' },
      { value: 'quero_bonus', label: '🔔 Sim, e quero receber os bônus de quem entra rápido' },
      { value: 'quero_conhecer', label: '👀 Ainda tô conhecendo, mas quero saber mais' },
      { value: 'sem_interesse', label: '❌ Não tenho interesse em me inscrever agora' }
    ]
  }
];

const SurveyForm: React.FC<SurveyFormProps> = ({ email, lancamento = 1 }) => {
  const navigate = useNavigate();
  const [formState, dispatch] = useReducer(formReducer, initialFormState);
  const successSectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<Omit<SurveyResponse, 'id' | 'created_at'>>({
    email,
    programming_experience: undefined,
    challenges: [],
    main_goal: undefined,
    blocking_factor: '',
    notification_preference: undefined,
    lancamento
  });

  useEffect(() => {
    if (formState.isCompleted && successSectionRef.current) {
      const timeoutId = setTimeout(() => {
        successSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [formState.isCompleted]);

  const handleChange = (name: string, value: any) => {
    setFormData((prev: Omit<SurveyResponse, 'id' | 'created_at'>) => ({ ...prev, [name]: value }));
    if (name !== 'challenges' && name !== 'blocking_factor') {
      handleNext();
    }
  };

  const handleNext = () => {
    const currentQuestion = questions[formState.currentStep];
    
    if (currentQuestion.type === 'select' && !formData[currentQuestion.id]) {
      return;
    }
    if (currentQuestion.type === 'checkbox' && formData.challenges.length === 0) {
      return;
    }
    if (currentQuestion.type === 'textarea' && !formData[currentQuestion.id]) {
      return;
    }

    if (formState.currentStep < questions.length - 1) {
      dispatch({ type: 'NEXT_STEP' });
    }
  };

  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      // Validação dos campos obrigatórios
      if (!formData.programming_experience) {
        throw new Error('Por favor, selecione sua experiência com programação');
      }
      if (!formData.main_goal) {
        throw new Error('Por favor, selecione seu objetivo principal');
      }
      if (!formData.blocking_factor?.trim()) {
        throw new Error('Por favor, nos conte o que está te bloqueando');
      }
      if (!formData.notification_preference) {
        throw new Error('Por favor, selecione sua preferência de notificação');
      }
      if (formData.challenges.length === 0) {
        throw new Error('Por favor, selecione pelo menos um desafio');
      }

      const emailTrimmed = email.toLowerCase().trim().replace(/\s+/g, '');

      // Verifica se o email existe na tabela de leads
      const { data: leads, error: leadError } = await supabase
        .from('leads_lancamento')
        .select('id')
        .eq('email', emailTrimmed)
        .single();

      if (leadError && leadError.code !== 'PGRST116') {
        console.error('Erro ao verificar lead:', leadError);
        throw new Error(`Erro ao verificar sua inscrição: ${leadError.message}`);
      }

      if (!leads) {
        throw new Error('Você precisa se inscrever primeiro antes de responder o questionário.');
      }

      // Prepara os dados para envio
      const surveyData = {
        email: emailTrimmed,
        experiencia_programacao: formData.programming_experience,
        desafios: formData.challenges || [],
        objetivo_principal: formData.main_goal,
        fator_bloqueio: formData.blocking_factor.trim(),
        preferencia_notificacao: formData.notification_preference,
        lancamento
      };

      // Primeiro, verifica se já existe uma resposta
      const { data: existingResponse, error: searchError } = await supabase
        .from('respostas_pesquisa')
        .select('id')
        .eq('email', emailTrimmed)
        .eq('lancamento', lancamento)
        .single();

      let error;

      if (existingResponse?.id) {
        // Se existe, faz update
        const { error: updateError } = await supabase
          .from('respostas_pesquisa')
          .update(surveyData)
          .eq('id', existingResponse.id);
        
        error = updateError;
      } else {
        // Se não existe, faz insert
        const { error: insertError } = await supabase
          .from('respostas_pesquisa')
          .insert([surveyData]);
        
        error = insertError;
      }

      if (error) {
        console.error('Erro ao salvar respostas:', error);
        throw new Error(`Erro ao salvar suas respostas: ${error.message}`);
      }

      dispatch({ type: 'SET_COMPLETED', payload: true });
      
      // Limpa o formulário
      setFormData({
        email: '',
        programming_experience: undefined,
        challenges: [],
        main_goal: undefined,
        blocking_factor: '',
        notification_preference: undefined,
        lancamento: 1
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao processar o formulário';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const currentQuestion = questions[formState.currentStep];
  const progress = ((formState.currentStep + 1) / questions.length) * 100;

  const getCheckedState = (challenge: string) => {
    return formData.challenges.includes(challenge);
  };

  if (formState.isCompleted) {
    return (
      <div ref={successSectionRef} className="bg-white/10 backdrop-blur p-6 rounded-lg border border-white/20 max-w-2xl mx-auto">
        <div className="text-center space-y-6">
          <div className="w-16 h-16 bg-[#f2055c] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#ffffff] to-[#f20587] text-transparent bg-clip-text">
            Obrigado por suas respostas!
          </h2>
          
          <p className="text-white/80 text-lg">
            Agora você já pode entrar no grupo do WhatsApp e começar sua jornada.
          </p>

          <div className="bg-[#f2055c]/10 p-6 rounded-lg mt-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Por que entrar no grupo?
            </h3>
            <ul className="space-y-4 text-left">
              <li className="flex items-start gap-3">
                <span className="text-[#f2055c] mt-1">→</span>
                <span className="text-white/90">Receba materiais exclusivos e atualizações em primeira mão</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#f2055c] mt-1">→</span>
                <span className="text-white/90">Participe de discussões técnicas e tire suas dúvidas</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#f2055c] mt-1">→</span>
                <span className="text-white/90">Conecte-se com outros devs e amplie seu networking</span>
              </li>
            </ul>

            <a 
              href={WHATSAPP_GROUP_LINK}
              target="_blank" 
              rel="noopener noreferrer"
              className="mt-8 w-full bg-[#f2055c] text-white py-4 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#f2055c]/90 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Entrar no Grupo do WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur p-6 rounded-lg border border-white/20 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-[#ffffff] to-[#f20587] text-transparent bg-clip-text">
          ⚡ Me ajuda a te entregar mais na Java Dev Week?
        </h2>
        <p className="text-white/80 text-lg">
          Rapidinho, coisa de 1 minuto. Suas respostas vão me ajudar a adaptar o conteúdo pra você.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#f2055c] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-sm text-white/60 mt-2 text-right">
          {formState.currentStep + 1} de {questions.length}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="min-h-[300px] flex flex-col">
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">{currentQuestion.title}</h3>
            {currentQuestion.description && (
              <p className="text-white/70">{currentQuestion.description}</p>
            )}
          </div>

          <div className="flex-grow">
            {currentQuestion.type === 'select' && (
              <div className="space-y-3">
                {currentQuestion.options?.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`w-full text-left px-4 py-3 rounded transition-all duration-200 ${
                      formData[currentQuestion.id] === option.value
                        ? 'bg-[#f2055c] text-white'
                        : 'bg-white/5 hover:bg-white/10 text-white'
                    }`}
                    onClick={() => handleChange(currentQuestion.id, option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {currentQuestion.type === 'checkbox' && (
              <div className="space-y-3">
                {currentQuestion.options?.map(option => (
                  <label key={option.value} className="flex items-center space-x-3 p-3 bg-white/5 rounded hover:bg-white/10 transition-all duration-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={getCheckedState(option.value)}
                      onChange={(e) => {
                        const newChallenges = e.target.checked
                          ? [...formData.challenges, option.value]
                          : formData.challenges.filter(c => c !== option.value);
                        handleChange('challenges', newChallenges);
                      }}
                      className="rounded border-white/20 bg-white/5 text-[#f2055c] focus:ring-[#f2055c]"
                    />
                    <span className="text-white">{option.label}</span>
                  </label>
                ))}
              </div>
            )}

            {currentQuestion.type === 'textarea' && (
              <textarea
                value={formData[currentQuestion.id] as string}
                onChange={(e) => handleChange(currentQuestion.id, e.target.value)}
                className="w-full px-4 py-3 rounded bg-white/5 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#f2055c] min-h-[120px]"
                placeholder="Digite sua resposta aqui..."
              />
            )}
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t border-white/10">
          <Button
            type="button"
            variant="secondary"
            onClick={handleBack}
            disabled={formState.currentStep === 0}
          >
            <span className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </span>
          </Button>

          {formState.currentStep === questions.length - 1 ? (
            <Button 
              type="submit" 
              disabled={formState.isLoading || !formData[currentQuestion.id]}
            >
              {formState.isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enviando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Finalizar
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={
                (currentQuestion.type === 'checkbox' && formData.challenges.length === 0) ||
                (currentQuestion.type === 'select' && !formData[currentQuestion.id]) ||
                (currentQuestion.type === 'textarea' && !formData[currentQuestion.id])
              }
            >
              <span className="flex items-center gap-2">
                Próxima
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          )}
        </div>

        {formState.error && (
          <div className="text-red-400 text-sm text-center mt-4">
            {formState.error}
          </div>
        )}
      </form>
    </div>
  );
};

export default SurveyForm; 